// Minimal chains for thirdweb - replaces @thirdweb-dev/chains to reduce bundle size by ~1.4MB

type Chain = {
  chain: string;
  chainId: number;
  explorers?: { name: string; url: string; standard: string }[];
  faucets?: string[];
  features?: unknown[];
  icon?: { url: string; width: number; height: number; format: string };
  infoURL?: string;
  name: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  networkId: number;
  redFlags?: unknown[];
  rpc: string[];
  shortName: string;
  slip44?: number;
  slug: string;
  testnet: boolean;
};

// Helper to create minimal chain placeholder
const createMinimalChain = (name: string, chainId: number, slug: string, testnet = false): Chain => ({
  chain: name,
  chainId,
  name,
  nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
  networkId: chainId,
  rpc: [],
  shortName: slug,
  slug,
  testnet,
});

// Main chains used by the app
export const Polygon: Chain = {
  chain: "Polygon",
  chainId: 137,
  explorers: [{ name: "polygonscan", url: "https://polygonscan.com", standard: "EIP3091" }],
  faucets: [],
  features: [],
  icon: { url: "ipfs://QmcxZHpyJa8T4i63xqjPYrZ6tKrt55tZJpbXcjSDKuKaf9/polygon/512.png", width: 512, height: 512, format: "png" },
  infoURL: "https://polygon.technology/",
  name: "Polygon Mainnet",
  nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
  networkId: 137,
  redFlags: [],
  rpc: [
    "https://polygon.drpc.org",
    "https://tenderly.rpc.polygon.community",
    "https://polygon.publicnode.com",
    "https://polygon-mainnet.gateway.tatum.io/",
    "https://polygon-public.nodies.app/",
    "https://1rpc.io/matic",
    "https://rpc-mainnet.matic.quiknode.pro",
    "https://polygon.api.onfinality.io/public",
  ],
  shortName: "matic",
  slip44: 966,
  slug: "polygon",
  testnet: false,
};

export const PolygonAmoyTestnet: Chain = {
  chain: "Polygon",
  chainId: 80002,
  explorers: [{ name: "polygonamoy", url: "https://www.oklink.com/amoy", standard: "EIP3091" }],
  faucets: ["https://faucet.polygon.technology/"],
  features: [],
  icon: { url: "ipfs://QmcxZHpyJa8T4i63xqjPYrZ6tKrt55tZJpbXcjSDKuKaf9/polygon/512.png", width: 512, height: 512, format: "png" },
  infoURL: "https://polygon.technology/",
  name: "Polygon Amoy Testnet",
  nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
  networkId: 80002,
  redFlags: [],
  rpc: ["https://rpc-amoy.polygon.technology/", "https://polygon-amoy-bor-rpc.publicnode.com"],
  shortName: "amoy",
  slip44: 1,
  slug: "polygon-amoy-testnet",
  testnet: true,
};

// Placeholder chains required by @thirdweb-dev/sdk imports
export const Base = createMinimalChain("Base", 8453, "base");
export const BaseSepoliaTestnet = createMinimalChain("Base Sepolia", 84532, "base-sepolia", true);
export const Celo = createMinimalChain("Celo", 42220, "celo");
export const CeloAlfajoresTestnet = createMinimalChain("Celo Alfajores", 44787, "celo-alfajores", true);
export const Flag = createMinimalChain("Flag", 147, "flag");
export const FlagTestnet = createMinimalChain("Flag Testnet", 1147, "flag-testnet", true);
export const Mumbai = createMinimalChain("Mumbai", 80001, "mumbai", true);
export const OpSepoliaTestnet = createMinimalChain("OP Sepolia", 11155420, "op-sepolia", true);
export const Optimism = createMinimalChain("Optimism", 10, "optimism");
export const Zora = createMinimalChain("Zora", 7777777, "zora");
export const ZoraSepoliaTestnet = createMinimalChain("Zora Sepolia", 999999999, "zora-sepolia", true);

// Default chains array
export const defaultChains = [Polygon, PolygonAmoyTestnet];

// Placeholder patterns for RPC URL replacement
// eslint-disable-next-line no-template-curly-in-string
const THIRDWEB_API_KEY_PLACEHOLDER = "${THIRDWEB_API_KEY}";
// eslint-disable-next-line no-template-curly-in-string
const PLACEHOLDER_PREFIX = "${";

// Function to get valid RPC URLs for a chain
export function getValidChainRPCs(chain: Chain, clientId?: string, mode: "http" | "ws" = "http"): string[] {
  const processedRPCs: string[] = [];

  chain.rpc.forEach((rpc) => {
    if (mode === "http" && !rpc.startsWith("http")) return;
    if (mode === "ws" && !rpc.startsWith("ws")) return;

    if (rpc.includes(THIRDWEB_API_KEY_PLACEHOLDER)) {
      if (clientId) {
        processedRPCs.push(rpc.replace(THIRDWEB_API_KEY_PLACEHOLDER, clientId));
      }
    } else if (rpc.includes(PLACEHOLDER_PREFIX)) {
      // Skip RPCs with other placeholders (INFURA_API_KEY, ALCHEMY_API_KEY, etc.)
      return;
    } else {
      processedRPCs.push(rpc);
    }
  });

  return processedRPCs;
}

// Default export
const thirdwebChains = {
  Polygon,
  PolygonAmoyTestnet,
  Base,
  BaseSepoliaTestnet,
  Celo,
  CeloAlfajoresTestnet,
  Flag,
  FlagTestnet,
  Mumbai,
  OpSepoliaTestnet,
  Optimism,
  Zora,
  ZoraSepoliaTestnet,
  defaultChains,
  getValidChainRPCs,
};

export default thirdwebChains;
